/**
 * Right-hand drawer hosting the Profile, Record and Columns tabs.
 *
 * The installed design system has no drawer, so this is a bordered column built from
 * tokens around the DS `Tabs`. It sits beside the grid rather than over it, so the row
 * or column being inspected stays visible.
 */

import type { ReactNode } from 'react';
import { Tabs, Icon, useTheme, spacing, fontSize } from '@fti/design-system';

export type SidePanelTab = 'profile' | 'record' | 'columns';

interface SidePanelProps {
  tab: SidePanelTab;
  onTabChange: (tab: SidePanelTab) => void;
  onClose: () => void;
  profile: ReactNode;
  record: ReactNode;
  columns: ReactNode;
}

export const SIDE_PANEL_WIDTH = 400;

export function SidePanel({ tab, onTabChange, onClose, profile, record, columns }: SidePanelProps) {
  const { theme } = useTheme();
  return (
    <aside
      aria-label="Explorer details"
      style={{
        width: SIDE_PANEL_WIDTH,
        flexShrink: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderLeft: `1px solid ${theme.colors.border.default}`,
        background: theme.colors.background.default,
        minHeight: 0,
      }}
    >
      <Tabs value={tab} onChange={(v) => onTabChange(v as SidePanelTab)}>
        <div style={{ display: 'flex', alignItems: 'center', paddingRight: spacing.xs }}>
          <div style={{ flex: 1 }}>
            <Tabs.List aria-label="Explorer panels">
              <Tabs.Trigger value="profile">Profile</Tabs.Trigger>
              <Tabs.Trigger value="record">Record</Tabs.Trigger>
              <Tabs.Trigger value="columns">Columns</Tabs.Trigger>
            </Tabs.List>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close panel"
            title="Close (Esc)"
            style={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              padding: spacing.xs,
              color: theme.colors.text.secondary,
              display: 'flex',
            }}
          >
            <Icon name="close" size={16} />
          </button>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: spacing.sm, fontSize: fontSize.sm, minHeight: 0 }}>
          <Tabs.Content value="profile">{profile}</Tabs.Content>
          <Tabs.Content value="record">{record}</Tabs.Content>
          <Tabs.Content value="columns">{columns}</Tabs.Content>
        </div>
      </Tabs>
    </aside>
  );
}
